import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaEnvelope, FaClock, FaLock } from 'react-icons/fa';
import './Contact.css';
import { functionsUsCentral1, httpsCallable } from '../firebase/config';
import Loader from '../components/Loader';
import SuccessPopup from '../components/SuccessPopup';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
  
    try {
      const sendMail = httpsCallable(functionsUsCentral1, "sendMail"); // 👈 name of your deployed function
      const result = await sendMail(formData);
  
      setShowSuccessPopup(true);
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      console.error("Email send failed:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Loader */}
      <Loader isLoading={isSubmitting} />
      
      {/* Success Popup */}
      <SuccessPopup 
        isVisible={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        message="We'll get back to you soon."
      />
      
      <div className="contact">
        <div className="container">
          {/* Luxury Hero Section */}
          <section className="contact-hero">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="hero-content"
            >
              <h1>Contact Us</h1>
              <p>Connect with our premium bespoke team</p>
            </motion.div>
          </section>

          <div className="contact-content">
            {/* Contact Information Card */}
            <motion.div 
              className="contact-info-card"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="card-header">
                <h2>Get in Touch</h2>
                <p>We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
              </div>
              
              <div className="contact-methods">
                <div className="contact-method">
                  <div className="method-icon">
                    <FaMapMarkerAlt />
                  </div>
                  <div className="method-content">
                    <h3>Address</h3>
                    <p>PROPERTY NO. 55 THIRD FLOOR<br />SAIDULAJAB NEW DELHI 110030<br />NEAR WESTEND MARGINE-2</p>
                  </div>
                </div>
                
                <div className="contact-method">
                  <div className="method-icon">
                    <FaEnvelope />
                  </div>
                  <div className="method-content">
                    <h3>Email</h3>
                    <p>info@vastrebyvijh.com</p>
                  </div>
                </div>
                
                <div className="contact-method">
                  <div className="method-icon">
                    <FaClock />
                  </div>
                  <div className="method-content">
                    <h3>Business Hours</h3>
                    <div className="hours-block">
                      <span>Mon–Fri: 10AM–6PM</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form Card */}
            <motion.div 
              className="contact-form-card"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {/* Quick Contact Summary for Mobile */}
              <div className="quick-contact-summary">
                <div className="quick-contact-item">
                  <FaEnvelope className="quick-icon" />
                  <span>info@vastrebyvijh.com</span>
                </div>
                <div className="quick-contact-item">
                  <FaClock className="quick-icon" />
                  <span>Mon–Fri: 10AM–6PM</span>
                </div>
              </div>
              
              <div className="card-header">
                <h2>Send Message</h2>
                <p>Tell us about your bespoke requirements</p>
              </div>
              
              {/* Error Message - Success is shown in popup */}
              {submitStatus === 'error' && (
                <div className="message-error">
                  <p>❌ Failed to send message. Please try again or contact us directly.</p>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="luxury-form">
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input 
                    type="text" 
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name" 
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input 
                    type="email" 
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address" 
                    required
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea 
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your bespoke requirements..." 
                    rows="5"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                
                <button 
                  type="submit" 
                  className={`luxury-submit-btn ${isSubmitting ? 'submitting' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending Message...' : 'Send Message'}
                </button>
              </form>
              
              {/* Privacy Message */}
              <div className="privacy-message">
                <FaLock className="privacy-icon" />
                <span>Your information is secure and will never be shared</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact; 